import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Divider,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  EditOutlined as EditIcon,
  LocalShippingOutlined as TruckIcon,
  SearchOffOutlined as NotFoundIcon,
} from '@mui/icons-material';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import PageHeader from '../../components/PageHeader';
import { isValidSaudiPlate } from '../../components/PlateInput';
import TruckMainDataPanel from '../../components/transportation/TruckMainDataPanel';
import TruckRouteScheduleTab from '../../components/transportation/TruckRouteScheduleTab';
import VehicleFormDialog, { type VehicleFormState } from '../../components/transportation/VehicleFormDialog';
import { APP_SHAPE, elevatedPanelSx } from '../../constants/shape';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { useOrganizationStore } from '../../stores/organization.store';
import { useTruckStore } from '../../stores/transportation.store';
import { TruckStatus, type TruckSchema } from '../../types/transportation.type';
import {
  resolveTruckDetailsTab,
  resolveTruckDetailsViewState,
  type TruckDetailsTab,
} from './truckDetails.helpers';

function normalizeTruckFeatures(next: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of next) {
    const s = String(raw).trim();
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

function blankVehicleFormState(): VehicleFormState {
  return {
    name: '',
    capacity: 0,
    status: TruckStatus.AVAILABLE,
    data: {
      en_plate_number: '',
      ar_plate_number: '',
      car_model: '',
      vehicle_type: '',
      capacity_unit: undefined,
      empty_weight: undefined,
      fuel_consumption_km_per_l: undefined,
      engine_type: undefined,
      vin: '',
      price_per_trip: undefined,
      truck_features: [],
    },
  };
}

function toVehicleFormState(vehicle: TruckSchema): VehicleFormState {
  return {
    name: vehicle.name || '',
    capacity: vehicle.capacity || 0,
    status: vehicle.status,
    data: {
      en_plate_number: vehicle.data?.en_plate_number || '',
      ar_plate_number: vehicle.data?.ar_plate_number || '',
      car_model: vehicle.data?.car_model || '',
      vehicle_type: vehicle.data?.vehicle_type || '',
      capacity_unit: vehicle.data?.capacity_unit ?? undefined,
      empty_weight: typeof vehicle.data?.empty_weight === 'number' ? vehicle.data.empty_weight : undefined,
      fuel_consumption_km_per_l:
        typeof vehicle.data?.fuel_consumption_km_per_l === 'number' ? vehicle.data.fuel_consumption_km_per_l : undefined,
      engine_type: vehicle.data?.engine_type ?? undefined,
      vin: vehicle.data?.vin || '',
      price_per_trip: typeof vehicle.data?.price_per_trip === 'number' ? vehicle.data.price_per_trip : undefined,
      truck_features: Array.isArray(vehicle.data?.truck_features)
        ? normalizeTruckFeatures(vehicle.data.truck_features as string[])
        : [],
    },
  };
}

export default function TruckDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { truckId } = useParams<{ truckId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const {
    selectedTruck,
    selectedTruckLoading,
    selectedTruckError,
    selectedTruckNotFound,
    trucksLoading,
    fetchTruck,
    updateTruck,
  } = useTruckStore();
  const { canMutate } = useAuthPermissions();

  const [editOpen, setEditOpen] = useState(false);
  const [formData, setFormData] = useState<VehicleFormState>(blankVehicleFormState);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning';
  }>({ open: false, message: '', severity: 'success' });

  const orgId = selectedOrganization?.id;
  const canEdit = Boolean(orgId && canMutate(`recyclee.${orgId}.*.truck.write`));
  const tab = resolveTruckDetailsTab(searchParams.get('tab'));

  useEffect(() => {
    if (orgId && truckId) void fetchTruck(orgId, truckId);
  }, [orgId, truckId, fetchTruck]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const truckMatchesParam = Boolean(selectedTruck && selectedTruck.id === truckId);
  const viewState = resolveTruckDetailsViewState({
    hasOrganization: Boolean(selectedOrganization),
    isLoading: selectedTruckLoading,
    truckMatchesParam,
    notFound: selectedTruckNotFound,
  });

  if (viewState === 'no-organization') {
    return <Alert severity="info">{t('transportation.fleet.details.selectOrganization')}</Alert>;
  }

  if (viewState === 'loading') {
    return <LoadingSkeleton variant="cards" filters={false} cardCount={3} />;
  }

  if (viewState === 'not-found') {
    return (
      <Box>
        <PageHeader
          title={t('transportation.fleet.details.pageTitle')}
          onBack={() => navigate('/transportation/fleet')}
          backLabel={t('transportation.fleet.details.backLabel')}
          sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        />
        <Paper variant="outlined" sx={{ p: { xs: 3, sm: 6 }, textAlign: 'center', borderRadius: `${APP_SHAPE.radiusLg}px` }}>
          <NotFoundIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />
          <Typography variant="h5">{t('transportation.fleet.details.notFoundTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            {t('transportation.fleet.details.notFoundHint')}
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/transportation/fleet')}>
            {t('transportation.fleet.details.backLabel')}
          </Button>
        </Paper>
      </Box>
    );
  }

  if (viewState === 'error') {
    return (
      <Box>
        <PageHeader
          title={t('transportation.fleet.details.pageTitle')}
          onBack={() => navigate('/transportation/fleet')}
          backLabel={t('transportation.fleet.details.backLabel')}
          sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        />
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => orgId && truckId && void fetchTruck(orgId, truckId)}>
              {t('common.retry')}
            </Button>
          }
        >
          {selectedTruckError || t('transportation.fleet.details.loadError')}
        </Alert>
      </Box>
    );
  }

  const truck = selectedTruck as TruckSchema;

  const openEdit = () => {
    setFormData(toVehicleFormState(truck));
    setFormErrors({});
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setFormErrors({});
  };

  const handleEditSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const parts = formData.data.en_plate_number.split(' ');
    const letters = parts[0] || '';
    const numbers = parts[1] || '';

    if (!isValidSaudiPlate(letters, numbers)) {
      showSnackbar(t('transportation.fleet.invalidPlate'), 'error');
      return;
    }

    const errs: Record<string, string> = {};
    if (!formData.name.trim()) errs.name = t('transportation.fleet.validation.nameRequired');
    if (!(formData.capacity > 0)) errs.capacity = t('transportation.fleet.validation.capacityRequired');
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const result = await updateTruck(truck.id, formData);
    if (result) {
      showSnackbar(t('transportation.fleet.vehicleUpdated'), 'success');
      closeEdit();
    } else {
      showSnackbar(t('transportation.fleet.vehicleUpdateFailed'), 'error');
    }
  };

  return (
    <Box>
      <PageHeader
        icon={<TruckIcon />}
        title={truck.name}
        subtitle={t('transportation.fleet.details.subtitle')}
        onBack={() => navigate('/transportation/fleet')}
        backLabel={t('transportation.fleet.details.backLabel')}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        actions={
          canEdit ? (
            <Button variant="contained" startIcon={<EditIcon />} onClick={openEdit}>
              {t('transportation.fleet.editVehicle')}
            </Button>
          ) : undefined
        }
      />

      <Paper elevation={1} sx={{ ...elevatedPanelSx, minWidth: 0 }}>
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, next: TruckDetailsTab) => setSearchParams({ tab: next }, { replace: true })}
            aria-label={t('transportation.fleet.details.tabsLabel')}
          >
            <Tab
              value="details"
              label={t('transportation.fleet.details.tabs.mainData')}
              id="truck-details-tab"
              aria-controls="truck-details-panel"
            />
            <Tab
              value="schedule"
              label={t('transportation.fleet.details.tabs.routeSchedule')}
              id="truck-schedule-tab"
              aria-controls="truck-schedule-panel"
            />
          </Tabs>
        </Box>
        <Divider />
        {tab === 'details' ? (
          <Box role="tabpanel" id="truck-details-panel" aria-labelledby="truck-details-tab" sx={{ p: { xs: 2, sm: 3 } }}>
            <TruckMainDataPanel truck={truck} organizationName={selectedOrganization?.name ?? ''} />
          </Box>
        ) : (
          <Box role="tabpanel" id="truck-schedule-panel" aria-labelledby="truck-schedule-tab" sx={{ p: { xs: 2, sm: 3 } }}>
            <TruckRouteScheduleTab truckId={truck.id} />
          </Box>
        )}
      </Paper>

      <VehicleFormDialog
        open={editOpen}
        onClose={closeEdit}
        isEdit
        formData={formData}
        setFormData={setFormData}
        formErrors={formErrors}
        loading={trucksLoading}
        canSubmit={canEdit}
        onSubmit={handleEditSubmit}
        onPlateError={(msg) => showSnackbar(msg, 'warning')}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
